import { Observable, forkJoin, interval, of, zip, from } from "rxjs";
import { catchError, every, first, map, mergeMap, retryWhen, switchMap, tap } from "rxjs/operators";
import _ from "lodash";

import { OldConditionReportVersionFetchedError } from "./errors";
import { SyncUpDependency } from "@skinsoft/classes-interfaces-definition";
import { AssociatedMedias, ConditionReport, ConditionReportStatePrefix, EntityType, MEDIA_SUBJECTS_KEYS, PushCachedValueResult, SynchroMediaUpdates } from "@skinsoft/types";
import { genericRetryStrategy } from "@skinsoft/rxjs-utils/generic-retry-strategy";
import { EntitySyncUpHandler } from "../../entity-sync-up-handler/entity-sync-up-handler";
import { ConditionReportOfflineDataUpdateCacher, OfflineDataUpdateCacher } from "@skinsoft/offline";
import { ConditionReportDataStore } from "@skinsoft/entity";
import { conditionReportSelectors } from "@skinsoft/selectors";
import { ImageTransferHelper, MediaTransferHelper } from "@skinsoft/media-transfer-helper";
import { cleanConditionReportBeforePush } from "./utils";
import { WemiSyncUpHandler } from "../wemi";

interface AdditionalSynchroData {
    alterationsMedia?: { media?: string }[], // there is an entry for each alteration, 
                                             // {} if the alteration does not have a local media,
                                             // {media: 'ID'} if the alteration has a local media
}

export class ConditionReportSyncUpHandler extends EntitySyncUpHandler<ConditionReport, ConditionReport, AdditionalSynchroData> {
    private additionalSynchroDataMap: {[id: string]: AdditionalSynchroData} = {}

    public static getInstance() {
        if (!ConditionReportSyncUpHandler.instance) {
            ConditionReportSyncUpHandler.instance = new ConditionReportSyncUpHandler()
        }

        return ConditionReportSyncUpHandler.instance
    }

    public static install(Vue) {
        ConditionReportSyncUpHandler.getInstance()
    }

    private static instance: ConditionReportSyncUpHandler = null as any

    private constructor() {
        super(
            new ConditionReportOfflineDataUpdateCacher(),
            ConditionReportDataStore.getInstance(),
            EntityType.CONDITION_REPORT
        )
    }

    getType(): EntityType {
        return this.type
    }

    getDependenciesIds(id: string): Observable<SyncUpDependency[]> {
        return super.getDependenciesIds(id).pipe(
            switchMap(superResult => {
                return zip(
                    of(superResult),
                    this.getCachedEntity(id)
                )
            }),
            switchMap(([superResult, cachedReport]) => {
                let result: SyncUpDependency[] = []

                const itemId: string = conditionReportSelectors.involvedItem(cachedReport as ConditionReport)
                if(itemId && OfflineDataUpdateCacher.isTemporaryId(itemId)) {
                    result.push({
                        id: itemId,
                        type: EntityType.WEMI,
                    })
                }

                result = result.concat(
                    this.getCachedAlterationsMediasIds(cachedReport).map(mediaId => {
                        return {
                            id: mediaId,
                            type: EntityType.MEDIA,
                        }
                    })
                )

                result = superResult.concat(result)

                return of(result)
            })
        )
    }

    getCachedAlterationsMediasIds(entity: ConditionReport): string[] {
        const res: string[] = []
        
        if(entity.conditionReportCS?.alterations && Array.isArray(entity.conditionReportCS.alterations)) {
            for(let alteration of entity.conditionReportCS.alterations) {
                if(alteration.media?.startsWith('local')){
                    res.push(alteration.media)
                }
            }
        }

        return res
    }

    /**
     * Modifies parameters
     * @param conditionReport 
     * @param additionalSynchroData 
     */
    private removeLocalMediaFromAlterations(conditionReport: ConditionReport, additionalSynchroData: AdditionalSynchroData) {
        additionalSynchroData.alterationsMedia = []
        if(conditionReport.conditionReportCS?.alterations && Array.isArray(conditionReport.conditionReportCS.alterations)) {
            for(let alteration of conditionReport.conditionReportCS.alterations) {
                if(alteration.media?.startsWith('local')){
                    additionalSynchroData.alterationsMedia.push({ media: alteration.media })
                    alteration.media = null
                    continue
                }

                additionalSynchroData.alterationsMedia.push({ })
            }
        }
    }

    removeLocalMedias(entity: ConditionReport): ConditionReport {
        let cleanedEntity = super.removeLocalMedias(entity)

        let additionalSynchroData: AdditionalSynchroData = {}
        this.removeLocalMediaFromAlterations(cleanedEntity, additionalSynchroData)
        this.additionalSynchroDataMap[cleanedEntity.id] = additionalSynchroData

        return cleanedEntity
    }

    /**
     * Put cached medias in condition-report again (used when generating report) 
     * @param entity 
     * @param additionalSynchroData 
     * @returns A clone of entity with the local medias
     */
    addLocalMedias(entity: ConditionReport, additionalSynchroData: AdditionalSynchroData, cachedMedias: AssociatedMedias): ConditionReport {
        let res = _.cloneDeep(entity)
        
        if(cachedMedias?.first) {
            _.set(res, 'associatedMedias.first', cachedMedias.first)
        }
        if(cachedMedias?.second) {
            _.set(res, 'associatedMedias.second', cachedMedias.second)
        }
        if(cachedMedias?.third) {
            _.set(res, 'associatedMedias.third', cachedMedias.third)
        }

        if(cachedMedias?.medias?.length) {
            if(res.associatedMedias?.medias?.length) {
                _.set(
                    res,
                    'associatedMedias.medias',
                    res.associatedMedias?.medias?.concat(cachedMedias.medias)
                )
            } else {
                _.set(res, 'associatedMedias.medias', cachedMedias.medias)
            }

            if(additionalSynchroData?.alterationsMedia) {
                // updating alterations medias
                if(res.conditionReportCS?.alterations) {
                    for(let i=0; i<additionalSynchroData.alterationsMedia.length; i++) {
                        const mediaId = additionalSynchroData.alterationsMedia[i].media
                        if(mediaId) {
                            res.conditionReportCS.alterations[i].media = mediaId
                        }
                    }
                }
            }
        }

        return res
    }

    formatDataBeforePush(entity: ConditionReport): Observable<ConditionReport> {
        return super.formatDataBeforePush(entity).pipe(
            switchMap(superReport => {
                return this.updateAlterationsMediasIds(superReport)
            }),
        )
    }

    getAndFormatModificationsBeforeMerge(localEntity: ConditionReport): Observable<Partial<ConditionReport>> {
        let formattedReport = cleanConditionReportBeforePush(localEntity)

        let linkedItem = conditionReportSelectors.involvedItem(localEntity)
        if(linkedItem) {
            this.replaceCreatedItemIdWithNewId(
                formattedReport as ConditionReport,
                linkedItem
            )
        }
        
        return of(formattedReport)
    }

    getAfterPushAdditionalData(dataToPush: SynchroMediaUpdates<ConditionReport>): Observable<AdditionalSynchroData> {
        const additionalData = this.additionalSynchroDataMap[dataToPush.id]
        return of(additionalData)
    }

    applyAfterPushOperations(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<ConditionReport>, additionalSynchroData?: AdditionalSynchroData): Observable<PushCachedValueResult> {
        return this.generateAndUploadPdfIfNeeded(pushResult, dataPushed)
            .pipe(
                switchMap((res: PushCachedValueResult) => {
                    if(res && res.success) {
                        return of(res)//this.handleReplaySubjects(res, dataPushed, additionalSynchroData)
                    }

                    return of({
                        success: false
                    })
                })
            )
    }
    
    private handleReplaySubjects(pushResult: PushCachedValueResult, dataToPush: SynchroMediaUpdates<ConditionReport>, additionalSynchroData?: AdditionalSynchroData): Observable<PushCachedValueResult> {
        const error$ = of({
            ...pushResult,
            success: false,
        })
        
        let reportId = pushResult.newId ?? dataToPush.id
        if(OfflineDataUpdateCacher.isTemporaryId(reportId)) {
            return error$
        }

        if(!reportId) {
            return error$
        }

        let replaySubjectsResults = [of({ success: true } as PushCachedValueResult)]

        const imageSubject = dataToPush.mediaSubjects[MEDIA_SUBJECTS_KEYS.image]
        
        const timer = interval(1000)

        let newFirstMediaId: string | null = null
    
        if(imageSubject) {
            // combination zip + timer = time output from another source
            replaySubjectsResults.push(zip(imageSubject, timer).pipe(map(([a, _]) => a)).pipe(
                mergeMap((replaySubjectContent): Observable<PushCachedValueResult> => {
                    if (replaySubjectContent.newMediaId) {
                        return (this.dataStore as ConditionReportDataStore).addMedia(
                            reportId, 
                            replaySubjectContent as { newMediaId: string }, 
                            true
                        ).pipe(
                            tap(() => {
                                ImageTransferHelper.getInstance().deleteImageInCache(replaySubjectContent.newMediaId as string)
                                if(replaySubjectContent.cacheKeys) {
                                    for(const cacheKey of replaySubjectContent.cacheKeys) {
                                        if(dataToPush.cachedMedias.first && replaySubjectContent.cacheKeys.includes(dataToPush.cachedMedias.first)) {
                                            newFirstMediaId = replaySubjectContent.newMediaId as string
                                        }
                                        ImageTransferHelper.getInstance().deleteImageInCache(cacheKey)

                                        // updating 'alteration copy' localmedias id
                                        additionalSynchroData?.alterationsMedia?.forEach(alteration => {
                                            if(alteration.media === cacheKey) {
                                                alteration.media = replaySubjectContent.newMediaId
                                            }
                                        })
                                    }
                                }
                            }),
                            tap(() => {
                                ImageTransferHelper.getInstance().addImageToReporterSubAlbum(replaySubjectContent.newMediaId)
                            }),
                            switchMap(() => of({
                                success: true
                            })),
                            catchError(err => {
                                return of({
                                    success: false
                                })
                            })
                        )
                    }

                    return of({
                        success: false
                    })
                }),
                every((result: PushCachedValueResult) => {
                    return result.success
                }),
                mergeMap(res => {
                    // fetching conditionReport (needed for updating first media)
                    return of({}).pipe(
                        switchMap(() => {
                            return this.dataStore.fetchOneOnlineMode(reportId)
                        }),
                        map(conditionReport => {
                            if(additionalSynchroData?.alterationsMedia?.length) {
                                if(conditionReport.conditionReportCS?.alterations?.length != additionalSynchroData?.alterationsMedia?.length) {
                                    // report not indexed with pushed changes yet
                                    throw new OldConditionReportVersionFetchedError()
                                }
                            }

                            return {
                                res,
                                conditionReport,
                            }
                        }),
                        retryWhen(genericRetryStrategy({
                            maxRetryAttempts: 5,
                            scalingDuration: [1000],
                            excludedStatusCodes: []
                        })),
                    )
                }),
                mergeMap(({res, conditionReport}) => {
                    const hasLocalMediaReferenceToUpdate: boolean = (newFirstMediaId != null || additionalSynchroData?.alterationsMedia != null)
                        
                    if(hasLocalMediaReferenceToUpdate) {
                        let body = {...conditionReport}

                        if(newFirstMediaId) {
                            // updating first media after every media has been uploaded to the API (successfully or not) to ensure that the associated 
                            // medias will not change during/after this operation (prevents data from being in an unknown/unstable state)
                            let updatedAssociatedMedias: AssociatedMedias

                            if(conditionReportSelectors.workflowState(dataToPush.body) === 'validated') {
                                const pdfId = body.associatedMedias.first
                                body.associatedMedias.first = null
                                updatedAssociatedMedias = MediaTransferHelper.permutateFirstMedia(newFirstMediaId, conditionReport.associatedMedias)
                                updatedAssociatedMedias = MediaTransferHelper.permutateFirstMedia(pdfId!, updatedAssociatedMedias)
                            } else {
                                updatedAssociatedMedias = MediaTransferHelper.permutateFirstMedia(newFirstMediaId, conditionReport.associatedMedias)
                            }
                                
                            _.set(body, 'associatedMedias', updatedAssociatedMedias)
                        }

                        if(additionalSynchroData?.alterationsMedia) {
                            // updating alterations medias
                            if(body.conditionReportCS?.alterations && additionalSynchroData.alterationsMedia) {
                                for(let i=0; i<additionalSynchroData.alterationsMedia.length; i++) {
                                    const newMediaId = additionalSynchroData.alterationsMedia[i].media
                                    if(newMediaId) {
                                        body.conditionReportCS.alterations[i].media = newMediaId
                                    }
                                }
                            }
                        }

                        return this.dataStore.update(reportId, body).pipe(
                            first(),
                            catchError(() => of(Error())),
                            switchMap(response => {
                                if(response instanceof Error) {
                                    return of(false)
                                }
                                return of(true)
                            }),                        
                            switchMap(requestRes => of(requestRes && res)),
                        )
                    }

                    return of(res)
                }),
                mergeMap(res => of({ success: res })),
                catchError(err => {
                    return of({ success: false })
                })
            ))
        }

        return forkJoin(replaySubjectsResults).pipe(
            switchMap((results) => {
                let finalResult = {
                    success: results.every(result => result.success) && pushResult.success,
                }
                if(pushResult.newId) {
                    (finalResult as any).newId = pushResult.newId
                }
                return of(finalResult)
            })
        )
    }
    
    private generateAndUploadPdfIfNeeded(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<ConditionReport>): Observable<PushCachedValueResult> {
        const error$ = of({
            ...pushResult,
            success: false,
        })

        const theRealThis = this
        
        let newReportId = pushResult.newId ?? dataPushed.id
        if(!newReportId) {
            return error$
        }
        if(OfflineDataUpdateCacher.isTemporaryId(newReportId)) {
            return error$
        }
        
        let result: Observable<PushCachedValueResult> = of(pushResult) 
        
        if(!pushResult.success) {
            return result
        }

        const workflowState = conditionReportSelectors.workflowState(dataPushed.body)

        if(workflowState === 'validated' || workflowState === ConditionReportStatePrefix + 'validated') {
            result = of({}).pipe(
                switchMap(x => {
                    return this.dataStore.fetchOneOnlineMode(newReportId)
                }),
                switchMap((onLineReport) => {
                    const onLineReportReady = this.checkFetchedOnlineReportContainsLastUpdates(
                        onLineReport, 
                        dataPushed.body
                    )

                    if(!onLineReportReady) {
                        throw new OldConditionReportVersionFetchedError()
                    }

                    // we need to use the offline report to generate the PDF in order to have access
                    // to the cached images that are not pushed on the server yet

                    // in case of offline created report, we need to retrieve the inventory number
                    // from the online report in order to display it on the PDF
                    const inventoryNumber = conditionReportSelectors.referenceNumber(onLineReport)
                    const additionalSynchroData = this.additionalSynchroDataMap[dataPushed.id]

                    let adaptedOfflineReport
                    if(dataPushed.cachedMedias && additionalSynchroData) {
                        adaptedOfflineReport = this.addLocalMedias(
                            dataPushed.body, 
                            additionalSynchroData, 
                            dataPushed.cachedMedias
                        )
                    } else {
                        adaptedOfflineReport = dataPushed.body
                    }

                    _.set(adaptedOfflineReport, 'conditionReportCS.referenceNumber', inventoryNumber)

                    // We also need to retrive the item id in case the item was created offline
                    this.replaceCreatedItemIdWithNewId(adaptedOfflineReport)

                    return zip(
                        (this.dataStore as ConditionReportDataStore).generateAndUploadPDF(
                            adaptedOfflineReport,
                            adaptedOfflineReport.conditionReportCS?.counterSignatures?.length ? true : false,
                            // We consider that reports without counter signatures don't have PDFs has it is the first time
                            // they are closed. They could contain a PDF if the status of the report was changed from Tryphon.
                            // We make this choice in order to speed up generation of PDF (it will avoid checking if medias are PDF)
                            theRealThis.locale
                        ),
                        of(onLineReport)
                    )
                }),
                retryWhen(genericRetryStrategy({
                    maxRetryAttempts: 5,
                })),
                switchMap(([pdfId, conditionReport]) => {
                    // set pdf as first media
                    const body = {...conditionReport}
                    let associatedMedias = conditionReport.associatedMedias
                    associatedMedias.medias = associatedMedias.medias ? associatedMedias.medias.concat([pdfId]) : [pdfId]
                    associatedMedias.medias = _.uniq(associatedMedias.medias)
                    const sortedMedias = MediaTransferHelper.permutateFirstMedia(pdfId, associatedMedias)
                    _.set(body, 'associatedMedias', sortedMedias)
                    return this.dataStore.update(newReportId, body)
                }),
                first(),
                // Handle success or error in the update and emit boolean
                catchError(e => {
                    return of(Error('Error during update...'))
                }),
                switchMap(
                    response => {
                        if (response instanceof Error) {
                            return of({
                                success: false
                            });
                        }
                        return of(pushResult)
                    }
                ),
            )
        }

        return result
    }

    private checkFetchedOnlineReportContainsLastUpdates(fetchedReport: ConditionReport, dataPushed: ConditionReport): boolean {
        const fetchedSigners = conditionReportSelectors.signers(fetchedReport)
        const pushedSigners = conditionReportSelectors.signers(dataPushed)

        if(fetchedSigners?.length || pushedSigners?.length) {
            if(fetchedSigners?.length !== pushedSigners?.length) {
                return false
            }
        }

        const fetchedAlterations = conditionReportSelectors.alterations(fetchedReport)
        const pushedAlterations = conditionReportSelectors.alterations(dataPushed)

        if(fetchedAlterations?.length || pushedAlterations?.length) {
            if(fetchedAlterations?.length !== pushedAlterations?.length) {
                return false
            }
        }

        const fetchedCounterSignatures = conditionReportSelectors.counterSignatures(fetchedReport)
        const pushedCounterSignatures = conditionReportSelectors.counterSignatures(dataPushed)

        if(fetchedCounterSignatures?.length || pushedCounterSignatures?.length) {
            if(fetchedCounterSignatures?.length !== pushedCounterSignatures?.length) {
                return false
            }
        }

        return true
    }

    /**
     * Updates the report passed as param
     * @param conditionReport
     */
    private replaceCreatedItemIdWithNewId(conditionReport: ConditionReport, itemIdParam?: string) {
        const itemId = itemIdParam ?? conditionReportSelectors.involvedItem(conditionReport)

        if(OfflineDataUpdateCacher.isTemporaryId(itemId)) {
            const onlineId = WemiSyncUpHandler.getInstance().getNewId(itemId)
    
            if(onlineId) {
                _.set(
                    conditionReport,
                    'conditionReportCS.linkableItem.linkableItem',
                    onlineId
                )
            }
        }
    }

    private updateAlterationsMediasIds(conditionReport: ConditionReport): Observable<ConditionReport> {        
        return of(null).pipe(
            switchMap(async () => {
                const updatedReport = _.cloneDeep(conditionReport)

                if(updatedReport.conditionReportCS?.alterations && Array.isArray(updatedReport.conditionReportCS.alterations)) {
                    for(const alteration of updatedReport.conditionReportCS.alterations) {
                        if(alteration.media?.startsWith('local')) {
                            const newId = await this.synchroUpIdMapping.getNewId(alteration.media)
                            if(newId) {
                                alteration.media = newId
                            }
                        }
                    }
                }

                return updatedReport
            })
        )
    }
}